import React, { useState, useCallback } from 'react';
import { Input, Button, Upload, Image, message as antMessage } from 'antd';
import { PaperClipOutlined, SendOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { uploadFile } from '../../../Messages/MessagesServices';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import { useTheme } from '../../../../Components/Shared/GlobalContext/ThemeContext';

const { TextArea } = Input;

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const MessageFooter = ({ onSendMessage, onUploadDocument, isSending }) => {
    const [message, setMessage] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const { handleError } = useErrorHandler();
    const { isDarkMode } = useTheme();

    const handleSendMessage = useCallback(() => {
        if (message.trim() || fileList.length > 0) {
            // Extract file IDs for attachments
            const attachment_ids = fileList
                .filter(file => file.fileId)
                .map(file => file.fileId);
                
            onSendMessage({ 
                message, 
                attachment_ids
            });
            
            setMessage('');
            setFileList([]);
        }
    }, [message, fileList, onSendMessage]);

    const handleFileUpload = async (file) => {
        setUploading(true);
        try {
            const { data } = await uploadFile(file);
            setUploading(false);
            
            const isImage = file.type.startsWith('image/');
            
            return {
                uid: `-${Date.now()}`,
                name: file.name,
                status: 'done',
                url: isImage ? data.file_url : null,
                fileId: data.id || null,
                fileData: data,
            };
        } catch (error) {
            setUploading(false);
            handleError(error);
            return null;
        }
    };

    const handlePreview = async (file) => {
        if (file.url) {
            setPreviewImage(file.url);
            setPreviewOpen(true);
            return;
        }

        if (!file.preview && file.originFileObj) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.preview);
        setPreviewOpen(true);
    };

    const handleChange = (info, file) => {
        if (info.file.status === 'removed') {
            setFileList(fileList.filter(item => item.uid !== file.uid));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div style={{
            padding: '12px 16px',
            borderTop: `1px solid ${isDarkMode ? '#434343' : '#eff3f4'}`,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: isDarkMode ? '#1f1f1f' : '#fff'
        }}>
            {fileList.length > 0 && (
                <div style={{
                    marginBottom: '12px',
                    width: '100%',
                }}>
                    <div style={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        padding: '4px',
                        WebkitOverflowScrolling: 'touch',
                    }}>
                        <div style={{
                            display: 'inline-flex',
                            gap: '8px',
                            paddingBottom: '10px',
                        }}>
                            {fileList.map((file) => (
                                <div key={file.uid} style={{
                                    width: '104px',
                                    height: '104px',
                                    flexShrink: 0,
                                }}>
                                    <Upload
                                        listType="picture-card"
                                        fileList={[file]}
                                        onPreview={() => handlePreview(file)}
                                        onChange={(info) => handleChange(info, file)}
                                        showUploadList={{
                                            showPreviewIcon: true,
                                            showRemoveIcon: true,
                                            showDownloadIcon: false,
                                        }}
                                        style={{
                                            width: '104px',
                                            height: '104px',
                                        }}
                                    />
                                </div>
                            ))}
                            {fileList.length < 8 && (
                                <div style={{
                                    width: '104px',
                                    height: '104px',
                                    flexShrink: 0,
                                }}>
                                    <div className="ant-upload-select" style={{
                                        width: '100%',
                                        height: '100%',
                                    }}>
                                        <Upload
                                            showUploadList={false}
                                            beforeUpload={async (file) => {
                                                const uploadedFile = await handleFileUpload(file);
                                                if (uploadedFile) {
                                                    setFileList([...fileList, uploadedFile]);
                                                }
                                                return false;
                                            }}
                                        >
                                            <div style={{ 
                                                width: '104px',
                                                height: '104px',
                                                border: `1px dashed ${isDarkMode ? '#434343' : '#d9d9d9'}`,
                                                borderRadius: '8px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: uploading ? 'not-allowed' : 'pointer',
                                                backgroundColor: isDarkMode ? '#2a2a2a' : undefined
                                            }}>
                                                {uploading ? (
                                                    <LoadingOutlined style={{color: isDarkMode ? '#fff' : undefined}} />
                                                ) : (
                                                    <>
                                                        <PlusOutlined style={{color: isDarkMode ? '#fff' : undefined}} />
                                                        <div style={{ marginTop: 8, color: isDarkMode ? '#fff' : undefined }}>Upload</div>
                                                    </>
                                                )}
                                            </div>
                                        </Upload>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '12px'
            }}>
                <Upload
                    showUploadList={false}
                    beforeUpload={async (file) => {
                        const uploadedFile = await handleFileUpload(file);
                        if (uploadedFile) {
                            setFileList([...fileList, uploadedFile]);
                        }
                        return false;
                    }}
                >
                    <Button 
                        icon={uploading ? <LoadingOutlined /> : <PaperClipOutlined />}
                        type="text"
                        style={{
                            color: '#1DA1F2',
                            border: 'none',
                            padding: '4px 8px',
                            height: '32px'
                        }}
                        disabled={uploading}
                    />
                </Upload>

                <TextArea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Start a message"
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    style={{
                        flex: 1,
                        border: 'none',
                        resize: 'none',
                        padding: '8px 0',
                        fontSize: '15px',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : undefined
                    }}
                />

                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    loading={isSending}
                    disabled={isSending}
                    style={{
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#1DA1F2',
                        border: 'none'
                    }}
                />
            </div>

            {previewImage && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
        </div>
    );
};

export default MessageFooter;